// 📄 pages/Mensajes/NuevoChatModal.tsx
// Iniciar un chat nuevo — elegir contexto (Proyecto u Organización) y
// destinatario(s). Decisiones de Fase 0: en Organización solo 1:1 (grupos
// son exclusivos de Proyecto); el candidato se elige de una lista ya
// conocida (miembros del proyecto/org), no hay búsqueda libre por email
// contra toda la plataforma (v1, ver memoria del chat).
import { useEffect, useState } from 'react';
import { X, Send, RefreshCw, Users, Building2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { Project, listProjectsForUser } from '../../services/projectService.ts';
import { getOrgMembers, OrgMemberConNombre } from '../../services/organizationService.ts';
import { Chat, ChatContexto } from '../../types/chat.ts';
import { crearChatConPrimerMensaje, encontrarChatDirectoExistente } from '../../services/chatService.ts';

interface NuevoChatModalProps {
  chatsExistentes: Chat[];
  onClose: () => void;
  onChatListo: (chatId: string) => void;
}

export default function NuevoChatModal({ chatsExistentes, onClose, onChatListo }: NuevoChatModalProps) {
  const { user, orgActual } = useAuth();

  const [contexto, setContexto] = useState<ChatContexto>('proyecto');

  const [proyectos, setProyectos] = useState<Project[]>([]);
  const [loadingProyectos, setLoadingProyectos] = useState(true);
  const [proyectoId, setProyectoId] = useState('');

  const [orgMembers, setOrgMembers] = useState<OrgMemberConNombre[]>([]);
  const [loadingOrgMembers, setLoadingOrgMembers] = useState(false);

  const [esGrupo, setEsGrupo] = useState(false);
  const [nombreGrupo, setNombreGrupo] = useState('');
  const [seleccionados, setSeleccionados] = useState<string[]>([]);
  const [texto, setTexto] = useState('');

  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    listProjectsForUser(user.uid)
      .then((lista) => {
        setProyectos(lista);
        setProyectoId((prev) => prev || lista[0]?.id || '');
      })
      .catch((err) => console.error('Error cargando tus proyectos:', err))
      .finally(() => setLoadingProyectos(false));
  }, [user]);

  useEffect(() => {
    if (contexto !== 'organizacion' || !orgActual) { setOrgMembers([]); return; }
    setLoadingOrgMembers(true);
    getOrgMembers(orgActual.id)
      .then(setOrgMembers)
      .catch((err) => console.error('Error cargando miembros de la organización:', err))
      .finally(() => setLoadingOrgMembers(false));
  }, [contexto, orgActual]);

  useEffect(() => {
    setSeleccionados([]);
    setEsGrupo(false);
    setNombreGrupo('');
  }, [contexto, proyectoId]);

  if (!user) return null;

  const proyectoSeleccionado = proyectos.find((p) => p.id === proyectoId) || null;

  // Candidatos: miembros del proyecto elegido, sin clientes (excluidos del
  // chat interno por rules) ni inactivos, sin uno mismo.
  const candidatosProyecto = (proyectoSeleccionado?.miembros || [])
    .filter((m) => m.rol !== 'cliente' && m.activo !== false && m.userId !== user.uid)
    .map((m) => ({ uid: m.userId, label: m.email }));

  const candidatosOrg = orgMembers
    .filter((m) => m.id !== user.uid)
    .map((m) => ({ uid: m.id, label: m.displayName }));

  const candidatos = contexto === 'proyecto' ? candidatosProyecto : candidatosOrg;

  const toggleSeleccionado = (uid: string) => {
    if (contexto === 'organizacion' || !esGrupo) {
      // Directo: un solo destinatario, elegir otro reemplaza al anterior.
      setSeleccionados((prev) => (prev[0] === uid ? [] : [uid]));
    } else {
      setSeleccionados((prev) => (prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!texto.trim()) { setError('Escribí un mensaje para empezar la conversación.'); return; }
    if (seleccionados.length === 0) { setError('Elegí al menos un destinatario.'); return; }
    if (esGrupo && !nombreGrupo.trim()) { setError('El grupo necesita un nombre.'); return; }

    const contextoId = contexto === 'proyecto' ? proyectoId : (orgActual?.id || '');
    if (!contextoId) { setError('No hay ningún contexto seleccionado.'); return; }

    const tipo = esGrupo ? 'grupo' : 'directo';
    const participantIds = [user.uid, ...seleccionados];

    // Evita duplicar un chat directo que ya existe con la misma persona en
    // el mismo contexto — lo reabre en vez de crear uno nuevo (ver
    // encontrarChatDirectoExistente, participantIds es inmutable).
    if (tipo === 'directo') {
      const existente = encontrarChatDirectoExistente(chatsExistentes, contexto, contextoId, user.uid, seleccionados[0]);
      if (existente) { onChatListo(existente.id); return; }
    }

    const participantesInfo: Record<string, string> = { [user.uid]: user.email || user.uid };
    for (const uid of seleccionados) {
      const candidato = candidatos.find((c) => c.uid === uid);
      if (candidato) participantesInfo[uid] = candidato.label;
    }

    setGuardando(true);
    try {
      const chatId = await crearChatConPrimerMensaje({
        tipo, contexto, contextoId, participantIds, participantesInfo,
        nombre: esGrupo ? nombreGrupo : '', autorUid: user.uid, textoPrimerMensaje: texto
      });
      onChatListo(chatId);
    } catch (err: any) {
      setError(err.message || 'Error al crear el chat.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">Nuevo chat</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setContexto('proyecto')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                contexto === 'proyecto' ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30' : 'text-gray-400 border border-transparent hover:bg-white/5'
              }`}
            >
              <Users className="w-3.5 h-3.5" /> Compañeros de proyecto
            </button>
            <button
              type="button"
              onClick={() => setContexto('organizacion')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                contexto === 'organizacion' ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30' : 'text-gray-400 border border-transparent hover:bg-white/5'
              }`}
            >
              <Building2 className="w-3.5 h-3.5" /> Compañeros de organización
            </button>
          </div>

          {contexto === 'proyecto' && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Proyecto</label>
              {loadingProyectos ? (
                <p className="text-xs text-gray-500 flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Cargando tus proyectos...</p>
              ) : proyectos.length === 0 ? (
                <p className="text-xs text-gray-500">No sos miembro de ningún proyecto todavía.</p>
              ) : (
                <select
                  value={proyectoId}
                  onChange={(e) => setProyectoId(e.target.value)}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white"
                >
                  {proyectos.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
              )}
            </div>
          )}

          {contexto === 'organizacion' && !orgActual && (
            <p className="text-xs text-amber-400">No tenés una organización activa seleccionada.</p>
          )}

          {contexto === 'proyecto' && proyectoId && (
            <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
              <input type="checkbox" className="accent-blue-500 w-3.5 h-3.5" checked={esGrupo} onChange={(e) => setEsGrupo(e.target.checked)} />
              Es un grupo (varios destinatarios)
            </label>
          )}

          {esGrupo && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Nombre del grupo</label>
              <input
                type="text"
                value={nombreGrupo}
                onChange={(e) => setNombreGrupo(e.target.value)}
                placeholder="ej. Equipo Commerce"
                className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              {esGrupo ? 'Destinatarios' : 'Destinatario'}
            </label>
            {(contexto === 'organizacion' && loadingOrgMembers) ? (
              <p className="text-xs text-gray-500 flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Cargando compañeros...</p>
            ) : candidatos.length === 0 ? (
              <p className="text-xs text-gray-500">No hay nadie más para elegir acá.</p>
            ) : (
              <div className="max-h-40 overflow-y-auto space-y-1 border border-white/10 rounded-lg p-2">
                {candidatos.map((c) => (
                  <label key={c.uid} className="flex items-center gap-2 text-sm text-gray-200 px-2 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer">
                    <input
                      type={esGrupo && contexto === 'proyecto' ? 'checkbox' : 'radio'}
                      name="destinatario"
                      className="accent-blue-500"
                      checked={seleccionados.includes(c.uid)}
                      onChange={() => toggleSeleccionado(c.uid)}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Mensaje</label>
            <textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Escribí el primer mensaje..."
              rows={3}
              className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={guardando}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
            >
              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {guardando ? 'Enviando...' : 'Iniciar chat'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
